/**
 * Feb-2026 speed pass: request de-duplication for `window.fetch`.
 *
 * Diagnostic (home page cold load) showed:
 *   • 6× /api/topics
 *   • 6× /api/calendar/upcoming
 *   • 4× /api/parshiot
 *   • 4× /api/videos/featured-series
 *   • 4× /api/library/state
 *
 * These duplicates come from independent components (Sidebar,
 * MobileBottomNav, HomePage, HolidayCountdownBanner, etc.) each running
 * their own `useEffect` fetch. Rather than refactor every call site to
 * share state, we wrap `window.fetch` once at boot so that any two
 * concurrent GETs to the same URL share a single in-flight promise.
 *
 * Rules:
 *   1. Only GET requests are deduped. Anything else (POST/PUT/DELETE)
 *      goes straight through.
 *   2. The dedupe key is `method|url|Accept-header|Authorization-header`
 *      so different callers with different auth still get their own
 *      response.
 *   3. Once the response resolves we clone the `Response` object per
 *      caller so each can independently call `.json()` / `.text()`.
 *   4. On error we drop the entry so retries don't stick to a bad hit.
 *
 * Idempotent — installing twice is a no-op.
 */

if (typeof window !== 'undefined' && !window.__vaadFetchDeduped) {
  window.__vaadFetchDeduped = true;

  const originalFetch = window.fetch.bind(window);
  /** @type {Map<string, Promise<Response>>} */
  const inflight = new Map();

  const keyFor = (input, init) => {
    const method = ((init && init.method) || (typeof input !== 'string' && input.method) || 'GET').toUpperCase();
    if (method !== 'GET') return null;
    const url = typeof input === 'string' ? input : input.url;
    // Only dedupe our own API. Never dedupe media/audio streams — they
    // may use range requests or byte-level offsets.
    if (!url.includes('/api/')) return null;
    if (url.includes('/api/stream') || url.includes('/api/download')) return null;
    const headers = (init && init.headers) || {};
    const accept = headers.Accept || headers.accept || '';
    const auth = headers.Authorization || headers.authorization || '';
    return `GET|${url}|${accept}|${auth}`;
  };

  window.fetch = function dedupedFetch(input, init) {
    const key = keyFor(input, init);
    if (!key) return originalFetch(input, init);

    const existing = inflight.get(key);
    if (existing) {
      // Return a fresh clone so consumers can read the body independently.
      return existing.then((r) => r.clone());
    }
    const promise = originalFetch(input, init)
      .then((response) => {
        // Only cache successful responses briefly (100ms) so that a burst
        // of duplicate mount-time fetches shares one network hit but
        // subsequent user-triggered fetches still get fresh data.
        setTimeout(() => inflight.delete(key), 100);
        // Store the response BEFORE returning so subsequent callers get
        // the same promise; clone for the primary caller.
        return response;
      })
      .catch((err) => {
        inflight.delete(key);
        throw err;
      });
    inflight.set(key, promise);
    return promise.then((r) => r.clone());
  };
}

export default null;
